import { enableProdMode, importProvidersFrom, LOCALE_ID } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, provideHttpClient, HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule, MAT_DATE_LOCALE, MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { registerLocaleData } from '@angular/common';
import localePt from '@angular/common/locales/pt';

import { provideTranslateService, TranslateLoader } from "@ngx-translate/core";
import { MultiTranslateHttpLoaderFactory } from './app/common/loaders/multi-translate-http-loader';

import { BlockUIModule } from 'ng-block-ui';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxGraphModule } from '@swimlane/ngx-graph';
import { SimpleNotificationsModule } from 'angular2-notifications';

import { AppComponent } from './app/app.component';
import { AppRoutes } from './app/app.routes';
import { environment } from './environments/environment';

// Providers
import { ConfigProvider, APPCONFIG, ConfigProviderFactory } from './app/common/providers/config.provider';
import { WINDOW_PROVIDERS } from './app/common/providers/window.provider';
import { RequestInterceptorService } from './app/modules/common/interceptors/request-interceptor';
import { AuthGuard } from './app/modules/common/auth.guard';
import { CustomAlertService } from './app/modules/common/service/custom-alert.service';
import { ProviderService } from './app/modules/common/service/provider.service';
import { NotificacaoService } from './app/modules/common/service/notificacao.service';
import { LanguageService } from './app/modules/common/service/language.service';
import { APP_INITIALIZER } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import Lara from '@primeng/themes/lara';

import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';

// HMR
import { hmrBootstrap } from './hmr';

// Translation loader - usando MultiTranslateHttpLoader para carregar múltiplos arquivos i18n
// Isso permite dividir as traduções por módulo, tornando a aplicação mais leve

// Registrar locale pt-BR
registerLocaleData(localePt);

// Formato de data personalizado DD/MM/YYYY
export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

if (environment.production) {
  enableProdMode();
}

const bootstrap = () => bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      RouterModule.forRoot(AppRoutes, {}),
      BrowserAnimationsModule,
      HttpClientModule,
      FormsModule,
      MatNativeDateModule,
      MatDialogModule,
      MatIconModule,
      MatSliderModule,
      MatListModule,
      MatSlideToggleModule,
      MatSelectModule,
      MatMenuModule,
      MatTooltipModule,
      NgxGraphModule,
      NgSelectModule,
      BlockUIModule.forRoot(),
      SimpleNotificationsModule.forRoot()
    ),
      provideAnimations(), // required animations providers
    provideToastr(), // Toastr providers
    provideHttpClient(),
    provideTranslateService({
      loader: {
        provide: TranslateLoader,
        useFactory: MultiTranslateHttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    ConfigProvider,
    WINDOW_PROVIDERS,
    AuthGuard,

    CustomAlertService,
    ProviderService,
    NotificacaoService,
    LanguageService,
    // Configuração de locale e formato de data
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    // Temporarily disabled to break circular dependency
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptorService, multi: true },
    { provide: APP_INITIALIZER, useFactory: ConfigProviderFactory, deps: [ConfigProvider], multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: (languageService: LanguageService) => () => languageService.initialize(),
      deps: [LanguageService],
      multi: true
    },
    { provide: APPCONFIG, useFactory: (provider: ConfigProvider) => provider.getConfig(), deps: [ConfigProvider] },
    providePrimeNG({
      theme: {
        preset: Lara,
        options: {
          darkModeSelector: false 
        }
      }      
    })
  ]
});

// HMR temporarily disabled for standalone migration
bootstrap();
